import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { NavDropdownLink } from '../components/nav/NavDropdown';
import { usePrerenderData } from './PrerenderDataContext';
import { getUseCaseNavLinks } from '../lib/useCaseSanity';
import { useSanityFetch } from '../lib/useSanityFetch';

type UseCaseNavContextValue = {
  links: readonly NavDropdownLink[];
  loading: boolean;
};

const UseCaseNavContext = createContext<UseCaseNavContextValue>({
  links: [],
  loading: false,
});

export function UseCaseNavProvider({ children }: { children: ReactNode }) {
  const prerenderLinks = usePrerenderData()?.useCaseNavLinks;
  const hasPrerenderLinks = prerenderLinks !== undefined;

  const { data: fetchedLinks, loading } = useSanityFetch(
    getUseCaseNavLinks,
    [],
    { enabled: !hasPrerenderLinks },
  );

  const value = useMemo<UseCaseNavContextValue>(
    () => ({
      links: prerenderLinks ?? fetchedLinks ?? [],
      loading: !hasPrerenderLinks && loading,
    }),
    [prerenderLinks, fetchedLinks, hasPrerenderLinks, loading],
  );

  return <UseCaseNavContext.Provider value={value}>{children}</UseCaseNavContext.Provider>;
}

export function useUseCaseNavLinks() {
  return useContext(UseCaseNavContext);
}
